/**
 * Interact with window
 * @module
 */
var _ = require('optimizely-client-fn');
var Logger = require('../utils/logger');

/**
 * @return {string}
 */
exports.getUserAgent = function(): string {
  return window.navigator.userAgent;
};

exports.getLocationSearch = function() {
  return window.location.search;
};

/**
 * Get the value of navigator.language or navigator.userLanguage (legacy browsers)
 * @returns {string|undefined}
 */
exports.getNavigatorLanguage = function(): string|undefined {
  return window.navigator.language || window.navigator.userLanguage;
};

exports.getHref = function() {
  return window.location.href;
};

/**
 *
 * @returns {Location}
 */
exports.getLocation = function(): Location {
  return window.location;
};

/**
 * Set location to given string. Replaces current page in browser history so user cannot go back to previous page.
 * @param {string} url Url to set
 */
exports.setLocation = function(url:string) {
  window.location.replace(url);
};

exports.setGlobal = function(name, value) {
  window[name] = value;
};

exports.getGlobal = function(name) {
  return window[name];
};

/**
 * Specific function to access member properties. Differs from getGlobal in case a variable name actually includes
 * a ".", rather than the period used as a property accessor. Yanked from
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/eval#Accessing_member_properties.
 *
 * @param {string} path
 */
exports.getGlobalByPath = function(path:string) {
  var keyPath = path.split('.');
  var windowObj: any = window;
  while (keyPath.length) {
    try {
      windowObj = windowObj[keyPath.shift()];
    } catch(e) {
      Logger.error('Attempted to access nonexistent property. Path ', path);
      throw new Error('Attempted to access nonexistent property. Path ' + path);
    }
  }
  return windowObj;
};

exports.addEventListener = function() {
  return window.addEventListener.apply(window, arguments);
};

exports.removeEventListener = function() {
  return window.removeEventListener.apply(window, arguments);
};

/**
 * Helper method to indicate if the MutationObserver API
 * is supported by the browser.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
 * @returns {boolean}
 */
exports.isMutationObserverAPISupported = function(): boolean {
  return !_.isUndefined(window.MutationObserver);
};

/**
 * Show an [alert](https://developer.mozilla.org/en-US/docs/Web/API/Window/alert) box
 * @param {string} msg
 */
exports.alert = function(msg:string) {
  alert(msg); // eslint-disable-line no-alert
};

/**
 * Wrapper for setTimeout, which catches and logs errors from the function.
 *
 * Doesn't support all the features of setTimeout; notably, fn must be a nullary function.
 *
 * @param {Function} fn Function to defer
 * @param {number} [timeout] ms to defer
 * @returns {number} ID of created timeout
 */
exports.setTimeout = function(fn:Function, timeout?:number): any {
  return setTimeout(function() { // eslint-disable-line no-restricted-globals
    try {
      fn();
    } catch(e) {
      Logger.warn('Deferred function threw error:', e);
    }
  }, timeout);
};

/**
 * Wrapper for setInterval, which catches and logs errors from the function.
 *
 * Doesn't support all the features of setInterval; notably, fn must be a nullary function.
 * @param {Function} fn Function to poll
 * @param {number} [interval] ms interval
 * @returns {number} ID of created interval
 */
exports.setInterval = function(fn:Function, interval?:number): any {
  return setInterval(function() { // eslint-disable-line no-restricted-globals
    try {
      fn();
    } catch(e) {
      Logger.warn('Polling function threw error:', e);
    }
  }, interval);
};
